import { useRef, useState } from "react";
import { toast } from "sonner";
import { useAddEvent } from "../../../../hooks/track/useAddEvent";
import { useUpdateEvent } from "../../../../hooks/track/useUpdateEvent";
import { useAnalytics } from "../../../../lib/analytics";
import { useClientTranslations } from "../../../../lib/i18n/client";
import {
	TRACK_EVENT_CREATED,
	TRACK_EVENT_EDITED,
} from "../../../../lib/track/analyticsEvents";
import { parseSyntheticEventId } from "../../../../lib/track/parseSyntheticEventId";
import {
	type CycleFormState,
	validateCycleForm,
} from "../../../../services/formValidationService";
import type { CreateEventInput, Event } from "../../../../types/event";
import { DatePicker, FormField, Select, type SelectOption } from "../fields";
import { RecurringSeriesBanner } from "../RecurringSeriesBanner";
import styles from "./CycleForm.module.css";
import { EventFormShell } from "./EventFormShell";

type FormMode = "create" | "edit";

interface CycleFormProps {
	mode: FormMode;
	initialValues?: Event;
	onClose: () => void;
}

// Curated cycle-day subtype keys (UI-SPEC §"Curated dropdown values").
const CYCLE_DAY_KEYS = [
	"day1PeriodStart",
	"stimulationDay1",
	"midStimMonitoring",
	"triggerDay",
	"retrievalDay",
	"transferDay",
	"betaDay",
	"other",
] as const;

function getDeviceTimezone(): string {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		return "America/New_York";
	}
}

/**
 * Phase 37 — Cycle event form (ADD-05 + EDM-04).
 *
 * Field order (UI-SPEC §"Body — form (per type) → CycleForm"):
 *   Cycle day → Date → Notes  (NO time field — cycle events are day-level).
 *
 * dateTime composition: `${YYYY-MM-DD}T00:00:00` — start-of-day wall-clock.
 * Title (Q4): subtypeLabel only (e.g. "Cycle Day 1").
 * `isRecurring` analytics value: always false (cycle events are one-off).
 *
 * Save lifecycle mirrors AppointmentTestForm (Plan 07):
 *   - Idempotency-Key via UUIDv4 in useRef (Pattern 2 — reset on success/error)
 *   - Edit mode: parseSyntheticEventId → baseId before PUT (Pitfall 2)
 *   - Analytics enums-only: { eventType: 'cycle', isRecurring: false }
 *   - Toast static i18n keys (no PHI interpolation)
 */
export function CycleForm({ mode, initialValues, onClose }: CycleFormProps) {
	const { t } = useClientTranslations("track");
	const { logTrack } = useAnalytics();

	const [state, setState] = useState<CycleFormState>(() => {
		if (initialValues) {
			return {
				subtype: initialValues.subtype ?? null,
				date: new Date(initialValues.dateTime),
				notes: initialValues.notes ?? "",
			};
		}
		return { subtype: null, date: null, notes: "" };
	});

	const idempotencyKeyRef = useRef<string | null>(null);
	const addMutation = useAddEvent();
	const updateMutation = useUpdateEvent();
	const isPending = addMutation.isPending || updateMutation.isPending;
	const validation = validateCycleForm(state);

	// Phase 37 D-19 — render whole-series banner above body in edit mode when synthetic id detected.
	// Cycle events are typically one-off (isRecurring=false in analytics), but a recurring
	// instance still renders the banner if a synthetic id slipped through.
	const isRecurringInstance =
		mode === "edit" && initialValues
			? parseSyntheticEventId(initialValues.id) !== null
			: false;

	const handleSave = () => {
		if (!validation.isValid) return;
		if (!state.date) return;

		const subtypeLabel = state.subtype
			? t(`event.add.subtypes.cycleDay.${state.subtype}`)
			: "";
		const yyyy = state.date.getFullYear();
		const mm = String(state.date.getMonth() + 1).padStart(2, "0");
		const dd = String(state.date.getDate()).padStart(2, "0");
		const dateTime = `${yyyy}-${mm}-${dd}T00:00:00`;

		const body: CreateEventInput = {
			type: "cycle",
			subtype: state.subtype,
			title: subtypeLabel,
			notes: state.notes.trim() || null,
			dateTime,
			timezone: initialValues?.timezone ?? getDeviceTimezone(),
		};

		if (mode === "create") {
			if (!idempotencyKeyRef.current) {
				idempotencyKeyRef.current = crypto.randomUUID();
			}
			addMutation.mutate(
				{ body, idempotencyKey: idempotencyKeyRef.current },
				{
					onSuccess: () => {
						idempotencyKeyRef.current = null;
						logTrack(TRACK_EVENT_CREATED, {
							eventType: "cycle",
							isRecurring: false,
						});
						toast.success(t("event.add.successToast"));
						onClose();
					},
					onError: () => {
						idempotencyKeyRef.current = null;
						toast.error(t("event.add.errors.save"));
					},
				},
			);
			return;
		}

		if (initialValues) {
			const baseId =
				parseSyntheticEventId(initialValues.id)?.baseId ?? initialValues.id;
			updateMutation.mutate(
				{ id: baseId, patch: body },
				{
					onSuccess: () => {
						logTrack(TRACK_EVENT_EDITED, {
							eventType: "cycle",
							isRecurring: !!initialValues.frequencyRRULE,
						});
						toast.success(t("event.edit.successToast"));
						onClose();
					},
					onError: () => {
						toast.error(t("event.edit.errorToast"));
					},
				},
			);
		}
	};

	const subtypeId = "cycle-subtype";
	const dateId = "cycle-date";
	const notesId = "cycle-notes";

	const cycleDayOptions: SelectOption[] = CYCLE_DAY_KEYS.map((key) => ({
		value: key,
		label: t(`event.add.subtypes.cycleDay.${key}`),
	}));

	return (
		<EventFormShell
			mode={mode}
			isValid={validation.isValid}
			isPending={isPending}
			onSave={handleSave}
			recurringBanner={
				isRecurringInstance && initialValues ? (
					<RecurringSeriesBanner type={initialValues.type} variant="edit" />
				) : undefined
			}
		>
			<FormField
				id={subtypeId}
				label={t("event.add.fields.subtype.cycle.label")}
				required
			>
				<Select
					id={subtypeId}
					value={state.subtype}
					options={cycleDayOptions}
					placeholder={t("event.add.fields.subtype.placeholder")}
					onChange={(v) => setState((s) => ({ ...s, subtype: v || null }))}
				/>
			</FormField>

			<DatePicker
				id={dateId}
				label={t("event.add.fields.date.label")}
				required
				selected={state.date ?? undefined}
				onSelect={(d) => setState((s) => ({ ...s, date: d ?? null }))}
			/>

			<FormField id={notesId} label={t("event.add.fields.notes.label")}>
				<textarea
					id={notesId}
					className={styles.textarea}
					value={state.notes}
					placeholder={t("event.add.fields.notes.placeholder")}
					onChange={(e) => setState((s) => ({ ...s, notes: e.target.value }))}
				/>
			</FormField>
		</EventFormShell>
	);
}
